import { editAditivoDescentralizadoRoute, editDistratoDescentralizadoRoute, newContratoDescentralizadoEditRoute } from "library/Routes";
import { CONTRATO_ETAPAS_PARAMS_URL, CONTRATO_FLUXO_DESCENTRALIZADO, CONTRATO_PASSOS, ContratoStatus, TipoContrato, URL_ENTE_DESCENTRALIZADO, VersaoContratoEnum } from "models/contratos";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { RootState } from "store";
import { loadContratoAssessor, openModalAditivo, openModalDistrato } from "store/modules/ContratosFluxoDescentralizado/Assessores/AssessoresActions";
import { loadContratoEntePublico, openModalEntePublicoAditivo, openModalEntePublicoDistrato } from "store/modules/ContratosFluxoDescentralizado/EntePublico/EntePublicoActions";
import SnackbarUtils from "utils/SnackbarUtils";
import { CreateRouteParams, EditRouteParams } from "pages/Contratos/Fluxos/FluxoDescentralizados/__types";
import { Box, CircularProgress, IconButton, Typography } from "@material-ui/core";
import AccordionLine from "assets/icons/AccordionLine";
import AccordionDownloadIcon from "assets/icons/AccordionDownloadIcon";
import { downloadDocumentoById } from "store/modules/AllContratos/AllContratosActions";
import { loadContratoEntePrivado, openModalEntePrivadoAditivo, openModalEntePrivadoDistrato } from "store/modules/ContratosFluxoDescentralizado/EntePrivado/EntePrivadoActions";
import { AxiosError, AxiosResponse } from "axios";

interface DetalhesContratoProps {
    idDocAssinado: number | null;
    idDocPlanoTrabalho: number | null;
    idDocExtrato: number | null;
    incluiPlanoTrabalho: boolean;
}

export const useAccordionDetalhe = (setExpanded: (
    panel: string | false) => void,
    id: number | null,
    versaoContrato: number,
    flgStatus: number,
    incluiPlanoTrabalho: boolean,
    dataAlteracao: string | undefined,
) => {
    const [downloadingDocuments, setDownloadingDocuments] = useState<{ [key: string]: boolean }>({});
    const languageValues = useSelector(
        (states: RootState) => states.languages.languageValues
    );
    const adendosAll = useSelector(
        (state: RootState) => state.allContratos.adendosAll
    );
    const adendoVersaoContratoUm = adendosAll.find(
        (adendo) => adendo.versaoContrato === 1
    );
    const idVersaoContrato = adendoVersaoContratoUm
        ? adendoVersaoContratoUm?.id
        : null;

    const params = useParams<CreateRouteParams & EditRouteParams>();
    const history = useHistory();
    const location = useLocation()
    const dispatch = useDispatch();

    const fluxoAssessor = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ASSESSOR);
    const fluxoEntePublico = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PUBLICO);
    const fluxoEntePrivado = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PRIVADO);

    const handleContratoEdit = () => {
        if (fluxoAssessor) {
            handleFluxoAssessor();
        } else if (fluxoEntePublico) {
            handleFluxoEntePublico();
        } else if (fluxoEntePrivado) {
            handleFluxoEntePrivado();
        }
    };

    const handleFluxoAssessor = () => {
        switch (versaoContrato) {
            case VersaoContratoEnum.CONTRATO:
                handleAssessorContrato();
                break;
            case VersaoContratoEnum.ADITIVO:
                handleAssessorAditivo();
                break;
            case VersaoContratoEnum.DISTRATO:
                handleAssessorDistrato();
                break;
            default:
                break;
        }
    };

    const handleAssessorContrato = () => {
        if (flgStatus === ContratoStatus.EM_EDICAO) {
            history.push(
                newContratoDescentralizadoEditRoute.getURLWithParams(
                    CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR,
                    CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                    id,
                    CONTRATO_PASSOS.DADOS_COOPERATIVA
                )
            );
            dispatch(loadContratoAssessor(id, VersaoContratoEnum.CONTRATO));
        }
        setExpanded('contratoPanel');
    };

    const handleAssessorAditivo = () => {
        history.push(
            editAditivoDescentralizadoRoute.getURLWithParams(
                CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR,
                CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                idVersaoContrato,
                CONTRATO_PASSOS.DOCUMENTOS,
                id,
                versaoContrato
            )
        );
        dispatch(openModalAditivo());
    };

    const handleAssessorDistrato = () => {
        history.push(
            editDistratoDescentralizadoRoute.getURLWithParams(
                CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR,
                CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                idVersaoContrato,
                CONTRATO_PASSOS.DOCUMENTOS,
                id,
                versaoContrato
            )
        );
        dispatch(openModalDistrato());
    };

    const handleFluxoEntePublico = () => {
        switch (versaoContrato) {
            case VersaoContratoEnum.CONTRATO:
                handleEntePublicoContrato();
                break;
            case VersaoContratoEnum.ADITIVO:
                handleEntePublicoAditivo();
                break;
            case VersaoContratoEnum.DISTRATO:
                handleEntePublicoDistrato();
                break;
            default:
                break;
        }
    };

    const handleEntePublicoContrato = useCallback(() => {
        if (flgStatus === ContratoStatus.EM_EDICAO) {
            history.push(
                newContratoDescentralizadoEditRoute.getURLWithParams(
                    CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PUBLICO,
                    CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                    id,
                    CONTRATO_PASSOS.DADOS_COOPERATIVA
                )
            );
            dispatch(loadContratoEntePublico(id, VersaoContratoEnum.CONTRATO));
        }
        setExpanded('contratoPanel');
    }, [dispatch, flgStatus, history, id, setExpanded]);

    const handleEntePublicoAditivo = useCallback(() => {
        history.push(
            editAditivoDescentralizadoRoute.getURLWithParams(
                CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PUBLICO,
                CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                idVersaoContrato,
                CONTRATO_PASSOS.DOCUMENTOS,
                id,
                versaoContrato
            )
        );
        dispatch(openModalEntePublicoAditivo())
    }, [dispatch, history, id, idVersaoContrato, versaoContrato]);

    const handleEntePublicoDistrato = useCallback(() => {
        history.push(
            editDistratoDescentralizadoRoute.getURLWithParams(
                CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PUBLICO,
                CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                idVersaoContrato,
                CONTRATO_PASSOS.DOCUMENTOS,
                id,
                versaoContrato
            )
        );
        dispatch(openModalEntePublicoDistrato());
    }, [dispatch, history, id, idVersaoContrato, versaoContrato]);

    const handleFluxoEntePrivado = () => {
        switch (versaoContrato) {
            case VersaoContratoEnum.CONTRATO:
                handleEntePrivadoContrato();
                break;
            case VersaoContratoEnum.ADITIVO:
                handleEntePrivadoAditivo();
                break;
            case VersaoContratoEnum.DISTRATO:
                handleEntePrivadoDistrato()
                break;
            default:
                break;
        }
    };

    const handleEntePrivadoContrato = useCallback(() => {
        if (flgStatus === ContratoStatus.EM_EDICAO) {
            history.push(
                newContratoDescentralizadoEditRoute.getURLWithParams(
                    CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO,
                    CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                    id,
                    CONTRATO_PASSOS.DADOS_COOPERATIVA
                )
            );
            dispatch(loadContratoEntePrivado(id, VersaoContratoEnum.CONTRATO));
        }
        setExpanded('contratoPanel');
    }, [dispatch, flgStatus, history, id, setExpanded]);

    const handleEntePrivadoAditivo = useCallback(() => {
        history.push(
            editAditivoDescentralizadoRoute.getURLWithParams(
                CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO,
                CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                idVersaoContrato,
                CONTRATO_PASSOS.DOCUMENTOS,
                id,
                versaoContrato
            )
        );
        dispatch(openModalEntePrivadoAditivo());
    }, [dispatch, history, id, idVersaoContrato, versaoContrato]);

    const handleEntePrivadoDistrato = useCallback(() => {
        history.push(
            editDistratoDescentralizadoRoute.getURLWithParams(
                CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO,
                CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
                idVersaoContrato,
                CONTRATO_PASSOS.DOCUMENTOS,
                id,
                versaoContrato
            )
        );
        dispatch(openModalEntePrivadoDistrato());
    }, [dispatch, history, id, idVersaoContrato, versaoContrato]);

    const handleDownload = (idDocAssinadoParam: number | null, docType: string, uniqueKey: string) => {
        if (idDocAssinadoParam === null) {
            return;
        } else {
            const contentType = 'application/pdf';
            const key = `${idDocAssinadoParam}_${docType}_${uniqueKey}`;
            setDownloadingDocuments(prevState => ({ ...prevState, [key]: true }));
            const onSuccess = (response: AxiosResponse) => {
                const blob = new Blob([response.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `documento_${idDocAssinadoParam}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setDownloadingDocuments(prevState => ({ ...prevState, [key]: false }));
            };
            const onErrorDownload = (error: AxiosError) => {
                SnackbarUtils.enqueueSnackbar({
                    message: error || languageValues.erroAoBaixarDocumento,
                    options: { variant: 'error' },
                });
                setDownloadingDocuments(prevState => ({ ...prevState, [key]: false }));
            };
            downloadDocumentoById(idDocAssinadoParam, contentType, "pdf", onSuccess, onErrorDownload)();
        }
    };

    const formatDateToLocale = (valueDate: string) => {
        const date = new Date(valueDate);
        return date.toLocaleDateString('pt-BR');
    };

    const extractYearFromDate = (valueDate: string) => {
        const date = new Date(valueDate);
        return date.getFullYear();
    };

    const getTipoEnte = () => {
        const tipoMapping = {
            [CONTRATO_FLUXO_DESCENTRALIZADO.ASSESSOR]: 1,
            [CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PUBLICO]: 2,
            [CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PRIVADO]: 3,
        };
        return tipoMapping[params.tipoContrato] || 0;
    };

    const getLabelContratoAssinadoEPlanoDeTrabalho = (versaoContratoParam: number) => {
        switch (versaoContratoParam) {
            case VersaoContratoEnum.CONTRATO:
                return languageValues.contratoAssinadoPlanoDeTrabalho;
            case VersaoContratoEnum.ADITIVO:
                return languageValues.aditivoAssinadoPlanoDeTrabalho;
            case VersaoContratoEnum.DISTRATO:
                return languageValues.distratoAssinadoPlanoDeTrabalho;
            default:
                return languageValues.contratoAssinadoPlanoDeTrabalho;
        }
    };

    const getLabelContrato = (versaoContratoParam: number) => {
        switch (versaoContratoParam) {
            case VersaoContratoEnum.CONTRATO:
                return languageValues.contratoAssinado;
            case VersaoContratoEnum.ADITIVO:
                return languageValues.aditivoAssinado;
            case VersaoContratoEnum.DISTRATO:
                return languageValues.distratoAssinado;
            default:
                return languageValues.contratoAssinado;
        }
    }

    const renderizaDetalhesContrato = ({ idDocAssinado, idDocPlanoTrabalho, idDocExtrato, }: DetalhesContratoProps) => {
        const verificaSeTemContratoAssinado = idDocAssinado !== null;
        const verificaSeTemPlanoDeTrabalho = idDocPlanoTrabalho !== null;
        const verificaSeTemExtrato = idDocExtrato !== null;

        const contratoAssinado = () => {
            const uniqueKey = 'contratoAssinado';
            const key = `${idDocAssinado}_${uniqueKey}`;
            return (
                <>
                    <Box id='contrato-assinado'>
                        <Typography variant='h5' className='accordionDetails__title'>
                            {getLabelContrato(versaoContrato)}
                        </Typography>
                    </Box>
                    <Box className='accordionDetails__message-box'>
                        {verificaSeTemContratoAssinado ? (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.documentoAssinado} - {extractYearFromDate(String(dataAlteracao))} - {languageValues.enviadoEm} {formatDateToLocale(String(dataAlteracao))}
                            </Typography>
                        ) : (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.cadastroContratoNaoHaDocumentos}
                            </Typography>
                        )}
                        <Box className='accordionDetails__box-icons'>
                            <AccordionLine />
                            {verificaSeTemContratoAssinado && downloadingDocuments[key] ? (
                                <CircularProgress size={24} />
                            ) : (
                                <IconButton onClick={() => handleDownload(idDocAssinado, 'contratoAssinado', uniqueKey)} color='primary' component='label'>
                                    <AccordionDownloadIcon color={verificaSeTemContratoAssinado ? '#8C5F0F' : ''} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </>
            );
        };

        const planoDeTrabalho = () => {
            const uniqueKey = 'planoDeTrabalho';
            const key = `${idDocPlanoTrabalho}_${uniqueKey}`;
            return (
                <>
                    <Box id='plano-de-trabalho'>
                        <Typography variant='h5' className='accordionDetails__title'>
                            {languageValues.cadastroContratoPlanoDeTrabalho}
                        </Typography>
                    </Box>
                    <Box className='accordionDetails__message-box'>
                        {verificaSeTemPlanoDeTrabalho ? (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.documentoAssinado} - {extractYearFromDate(String(dataAlteracao))} - {languageValues.enviadoEm} {formatDateToLocale(String(dataAlteracao))}
                            </Typography>
                        ) : (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.cadastroContratoNaoHaDocumentos}
                            </Typography>
                        )}
                        <Box className='accordionDetails__box-icons'>
                            <AccordionLine />
                            {verificaSeTemPlanoDeTrabalho && downloadingDocuments[key] ? (
                                <CircularProgress size={24} />
                            ) : (
                                <IconButton onClick={() => handleDownload(idDocPlanoTrabalho, 'planoDeTrabalho', uniqueKey)} color='primary' component='label'>
                                    <AccordionDownloadIcon color={verificaSeTemPlanoDeTrabalho ? '#8C5F0F' : ''} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </>
            );
        };

        const contratoAssinadoEPlanoDeTrabalho = () => {
            const uniqueKey = 'contratoAssinadoEPlanoDeTrabalho';
            const key = `${idDocAssinado}_${uniqueKey}`;
            return (
                <>
                    <Box id='contrato-assinado-e-plano-de-trabalho'>
                        <Typography variant='h5' className='accordionDetails__title'>
                            {getLabelContratoAssinadoEPlanoDeTrabalho(versaoContrato)}
                        </Typography>
                    </Box>
                    <Box className='accordionDetails__message-box'>
                        {verificaSeTemContratoAssinado ? (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.documentoAssinado} - {extractYearFromDate(String(dataAlteracao))} - {languageValues.enviadoEm} {formatDateToLocale(String(dataAlteracao))}
                            </Typography>
                        ) : (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.cadastroContratoNaoHaDocumentos}
                            </Typography>
                        )}
                        <Box className='accordionDetails__box-icons'>
                            <AccordionLine />
                            {idDocAssinado !== null && downloadingDocuments[key] ? (
                                <CircularProgress size={24} />
                            ) : (
                                <IconButton onClick={() => handleDownload(idDocAssinado, 'contratoAssinadoEPlanoDeTrabalho', uniqueKey)} color='primary' component='label'>
                                    <AccordionDownloadIcon color={verificaSeTemContratoAssinado ? '#8C5F0F' : ''} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </>
            );
        };

        const extrato = () => {
            const uniqueKey = 'extrato';
            const key = `${idDocExtrato}_${uniqueKey}`;
            return (
                <>
                    <Box id='extrato'>
                        <Typography variant='h5' className='accordionDetails__title'>
                            {languageValues.cadastroContratoExtrato}
                        </Typography>
                    </Box>
                    <Box className='accordionDetails__message-box'>
                        {verificaSeTemExtrato ? (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.documentoAssinado} - {extractYearFromDate(String(dataAlteracao))} - {languageValues.enviadoEm} {formatDateToLocale(String(dataAlteracao))}
                            </Typography>
                        ) : (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.cadastroContratoNaoHaDocumentos}
                            </Typography>
                        )}
                        <Box className='accordionDetails__box-icons'>
                            <AccordionLine />
                            {verificaSeTemExtrato && downloadingDocuments[key] ? (
                                <CircularProgress size={24} />
                            ) : (
                                <IconButton onClick={() => handleDownload(idDocExtrato, 'extrato', uniqueKey)} color='primary' component='label'>
                                    <AccordionDownloadIcon color={verificaSeTemExtrato ? '#8C5F0F' : ''} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </>
            );
        };

        const renderizacaoFinalEntePublico = () => (
            <>
                {incluiPlanoTrabalho ? contratoAssinadoEPlanoDeTrabalho() : contratoAssinado()}
                {!incluiPlanoTrabalho && planoDeTrabalho()}
                {extrato()}
            </>
        );

        switch (getTipoEnte()) {
            case TipoContrato.ASSESSOR:
            case TipoContrato.ENTE_PRIVADO:
                switch (versaoContrato) {
                    case VersaoContratoEnum.CONTRATO:
                    case VersaoContratoEnum.ADITIVO:
                    case VersaoContratoEnum.DISTRATO:
                        return contratoAssinado();
                    default:
                        return (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.errotipoContratoDesconhecido}
                            </Typography>
                        );
                }
            case TipoContrato.ENTE_PUBLICO:
                switch (versaoContrato) {
                    case VersaoContratoEnum.CONTRATO:
                    case VersaoContratoEnum.ADITIVO:
                    case VersaoContratoEnum.DISTRATO:
                        return renderizacaoFinalEntePublico()

                    default:
                        return (
                            <Typography className='accordionDetails__message-text'>
                                {languageValues.errotipoContratoDesconhecido}
                            </Typography>
                        );
                }
            default:
                return (
                    <Typography className='accordionDetails__message-text'>
                        {languageValues.errotipoContratoDesconhecido}
                    </Typography>
                );
        }
    };

    return {
        handleContratoEdit,
        renderizaDetalhesContrato
    }
}

